/**
 * Raíz del portal público: escucha el `history` y monta la pantalla que toque.
 *
 * Existe separada de `PublicApp` por una razón concreta de testeo: `PublicApp`
 * recibe la ruta **por prop** y por eso se puede renderizar con
 * `react-dom/server` sin DOM ni `window`, que es como lo prueba
 * `apps/portal/test/public-pages.test.tsx`. Todo lo que necesita browser —el
 * `popstate`, el `pushState`, el `lang` del documento— vive acá y en `hooks.ts`.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { LOCALE_TAGS, getLocale, subscribeLocale } from '../../i18n/index';
import { PublicApp } from './PublicApp';
import { usePublicRoute } from './hooks';
import { applyInitialPublicLocale } from './locale';

export function PublicRoot({
  base,
  apiUrl = '',
}: {
  base: string;
  apiUrl?: string;
}): JSX.Element | null {
  const route = usePublicRoute(base);
  const locale = useSyncExternalStore(subscribeLocale, getLocale, getLocale);

  useEffect(() => {
    applyInitialPublicLocale();
  }, []);

  // El `lang` del documento importa de verdad en un portal público: es lo que
  // usa el lector de pantalla para elegir la voz y el browser para ofrecer la
  // traducción. El `index.html` viene con `lang="es"` cableado, así que sin
  // esto un portal leído en inglés seguiría anunciándose como español.
  useEffect(() => {
    document.documentElement.lang = LOCALE_TAGS[locale];
  }, [locale]);

  // No debería pasar (`main.tsx` sólo monta esto con una ruta pública), pero un
  // `pushState` a una URL que ya no es pública no puede dejar la pantalla en
  // blanco: se recarga y manda el portal interno.
  if (route === null) {
    window.location.reload();
    return null;
  }

  return <PublicApp route={route} base={base} apiUrl={apiUrl} />;
}
